/**
 * <pdp-savings-badge>
 * Keeps the savings % + amount in sync when the selected variant changes.
 * Subscribes to the theme's pub/sub variantChange event (constants.js).
 *
 * Expects data attributes on the element:
 *   data-template         e.g. "Save {percent}% today"
 *   data-amount-template  e.g. "(you save {amount})"
 *   data-show-amount      "true" | "false"
 *
 * Hides itself (hidden attribute) when compare_at_price <= price.
 */
if (!customElements.get('pdp-savings-badge')) {
  customElements.define(
    'pdp-savings-badge',
    class PdpSavingsBadge extends HTMLElement {
      constructor() {
        super();
        this.percentEl = this.querySelector('[data-savings-percent]');
        this.amountEl = this.querySelector('[data-savings-amount]');
        this.template = this.dataset.template || 'Save {percent}% today';
        this.amountTemplate = this.dataset.amountTemplate || '(you save {amount})';
        this.showAmount = this.dataset.showAmount === 'true';
        this.moneyFormatter = new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          maximumFractionDigits: 0,
        });
      }

      connectedCallback() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.updateFromVariant(event && event.data && event.data.variant);
        });
      }

      disconnectedCallback() {
        if (typeof this.unsubscribe === 'function') this.unsubscribe();
      }

      updateFromVariant(variant) {
        if (!variant) {
          this.hideBadge();
          return;
        }
        const price = Number(variant.price);
        const compare = Number(variant.compare_at_price);
        if (!compare || compare <= price) {
          this.hideBadge();
          return;
        }
        const savingsAmount = compare - price;
        const percent = Math.round((savingsAmount / compare) * 100);
        this.renderPercent(percent);
        if (this.showAmount) this.renderAmount(savingsAmount);
        this.removeAttribute('hidden');
      }

      hideBadge() {
        this.setAttribute('hidden', '');
      }

      renderPercent(percent) {
        if (!this.percentEl) return;
        this.percentEl.textContent = this.template.replace('{percent}', String(percent));
      }

      renderAmount(amountCents) {
        if (!this.amountEl) return;
        const rupees = amountCents / 100;
        const formatted = this.moneyFormatter.format(rupees);
        this.amountEl.textContent = this.amountTemplate.replace('{amount}', formatted);
      }
    }
  );
}
